import { UnreachableFunction } from 'ui/base/types';

export enum BasePathItems {
  VIEW_ITEM,
  FILTER_ITEMS,
}

export type PagePathItems =
  | { kind: BasePathItems.VIEW_ITEM; itemID: string }
  | { kind: BasePathItems.FILTER_ITEMS };

export enum BasePath {
  INTRO,
  REGISTER,
  LOGIN,
  PASSWORD_RESET,
  PASSWORD_RESET_CONFIRMATION,
  EMAIL_VERIFICATION,
  ADD_PANGOLIN_SETUP,
  ADD_PANGOLIN_POWER,
  ADD_PANGOLIN_METHOD,
  ADD_PANGOLIN_MNEMONIC,
  ADD_PANGOLIN_RENAME,
  ADD_PANGOLIN_PROFILE,
  ADD_PANGOLIN_SUCCESS,
  HOME,
  PLANS,
  UPDATES,
  DEVICES_LIST,
  DEVICES_INFO,
  DEVICES_VIEW,
  DEVICES_EDIT,
  LIST_ISSUES,
  MORE,
  HISTORY_LIST,
  BANDWIDTH_LIST,
  BANDWIDTH_SETTINGS,
  SCHEDULE_LIST,
  SCHEDULE_ADD,
  SCHEDULE_VIEW,
  SCHEDULE_EDIT,
  VPN_LIST,
  VPN_ADD,
  VPN_EDIT,
  MANAGE_PANGOLIN_LIST,
  MANAGE_PANGOLIN_VIEW,
  MANAGE_PANGOLIN_EDIT,
  MANAGE_PANGOLIN_INTERCEPT,
  MANAGE_PANGOLIN_SCANNER,
  MANAGE_PANGOLIN_UNLINK,
  SETTINGS,
  SETTINGS_EDIT,
  SETTINGS_PASSWORD_CHANGE,
  SETTINGS_ACCOUNT_DELETE,
}

export type PagePath =
  | { kind: BasePath.INTRO }
  | { kind: BasePath.REGISTER }
  | { kind: BasePath.LOGIN }
  | { kind: BasePath.PASSWORD_RESET }
  | { kind: BasePath.PASSWORD_RESET_CONFIRMATION }
  | { kind: BasePath.EMAIL_VERIFICATION }
  | { kind: BasePath.ADD_PANGOLIN_SETUP }
  | { kind: BasePath.ADD_PANGOLIN_POWER }
  | { kind: BasePath.ADD_PANGOLIN_METHOD }
  | { kind: BasePath.ADD_PANGOLIN_MNEMONIC }
  | { kind: BasePath.ADD_PANGOLIN_RENAME; pangolinID: string }
  | { kind: BasePath.ADD_PANGOLIN_PROFILE; pangolinID: string }
  | { kind: BasePath.ADD_PANGOLIN_SUCCESS; pangolinID: string }
  | { kind: BasePath.HOME; pangolinID: string }
  | { kind: BasePath.PLANS; pangolinID: string }
  | { kind: BasePath.UPDATES; pangolinID: string }
  | { kind: BasePath.DEVICES_LIST; pangolinID: string }
  | { kind: BasePath.DEVICES_INFO; pangolinID: string }
  | { kind: BasePath.DEVICES_VIEW; pangolinID: string; deviceID: string }
  | { kind: BasePath.DEVICES_EDIT; pangolinID: string; deviceID: string }
  | { kind: BasePath.LIST_ISSUES; pangolinID: string; itemsPath?: PagePathItems }
  | { kind: BasePath.MORE; pangolinID: string }
  | { kind: BasePath.HISTORY_LIST; pangolinID: string; itemsPath?: PagePathItems }
  | { kind: BasePath.BANDWIDTH_LIST; pangolinID: string }
  | { kind: BasePath.BANDWIDTH_SETTINGS; pangolinID: string }
  | { kind: BasePath.SCHEDULE_LIST; pangolinID: string }
  | { kind: BasePath.SCHEDULE_ADD; pangolinID: string }
  | { kind: BasePath.SCHEDULE_VIEW; pangolinID: string; scheduleID: string }
  | { kind: BasePath.SCHEDULE_EDIT; pangolinID: string; scheduleID: string }
  | { kind: BasePath.VPN_LIST; pangolinID: string }
  | { kind: BasePath.VPN_ADD; pangolinID: string }
  | { kind: BasePath.VPN_EDIT; pangolinID: string; clientID: string }
  | { kind: BasePath.MANAGE_PANGOLIN_LIST }
  | { kind: BasePath.MANAGE_PANGOLIN_VIEW; pangolinID: string }
  | { kind: BasePath.MANAGE_PANGOLIN_EDIT; pangolinID: string }
  | { kind: BasePath.MANAGE_PANGOLIN_INTERCEPT; pangolinID: string }
  | { kind: BasePath.MANAGE_PANGOLIN_SCANNER; pangolinID: string }
  | { kind: BasePath.MANAGE_PANGOLIN_UNLINK; pangolinID: string }
  | { kind: BasePath.SETTINGS }
  | { kind: BasePath.SETTINGS_EDIT }
  | { kind: BasePath.SETTINGS_PASSWORD_CHANGE }
  | { kind: BasePath.SETTINGS_ACCOUNT_DELETE };

export type CoreBasePath = BasePath.HOME | BasePath.MORE | BasePath.DEVICES_LIST | BasePath.LIST_ISSUES;

export function pagePathToPathString(pathType: PagePath): string {
  switch(pathType.kind) {
    // Public Pages
    case BasePath.INTRO: {
      return '/';
    }
    case BasePath.REGISTER: {
      return '/register';
    }
    case BasePath.LOGIN: {
      return '/login';
    }
    case BasePath.PASSWORD_RESET: {
      return '/password-reset';
    }
    case BasePath.PASSWORD_RESET_CONFIRMATION: {
      return '/password-reset-confirmation';
    }
    case BasePath.EMAIL_VERIFICATION: {
      return '/email-verification';
    }
    // Add Pangolin
    case BasePath.ADD_PANGOLIN_SETUP: {
      return '/pangolin/add/setup';
    }
    case BasePath.ADD_PANGOLIN_POWER: {
      return '/pangolin/add/power';
    }
    case BasePath.ADD_PANGOLIN_METHOD: {
      return '/pangolin/add/method';
    }
    case BasePath.ADD_PANGOLIN_MNEMONIC: {
      return '/pangolin/add/mnemonic';
    }
    case BasePath.ADD_PANGOLIN_RENAME: {
      return `/pangolin/add/rename/${pathType.pangolinID}`;
    }
    case BasePath.ADD_PANGOLIN_PROFILE: {
      return `/pangolin/add/profile/${pathType.pangolinID}`;
    }
    case BasePath.ADD_PANGOLIN_SUCCESS: {
      return `/pangolin/add/success/${pathType.pangolinID}`;
    }
    // Core Pages
    case BasePath.HOME: {
      return `/pangolin/${pathType.pangolinID}`;
    }
    case BasePath.PLANS: {
      return `/pangolin/${pathType.pangolinID}/plans`;
    }
    case BasePath.UPDATES: {
      return `/pangolin/${pathType.pangolinID}/updates`;
    }
    case BasePath.DEVICES_LIST: {
      return `/pangolin/${pathType.pangolinID}/devices`;
    }
    case BasePath.DEVICES_INFO: {
      return `/pangolin/${pathType.pangolinID}/devices/info`;
    }
    case BasePath.DEVICES_VIEW: {
      return `/pangolin/${pathType.pangolinID}/devices/${pathType.deviceID}`;
    }
    case BasePath.DEVICES_EDIT: {
      return `/pangolin/${pathType.pangolinID}/devices/${pathType.deviceID}/edit`;
    }
    case BasePath.LIST_ISSUES: {
      if (pathType.itemsPath) {
        switch (pathType.itemsPath.kind) {
          case BasePathItems.FILTER_ITEMS:
            return `/pangolin/${pathType.pangolinID}/issues/filter`;
          case BasePathItems.VIEW_ITEM:
            return `/pangolin/${pathType.pangolinID}/issues/view/${pathType.itemsPath.itemID}`;
          default:
            return UnreachableFunction(pathType.itemsPath);
        }
      }
      return `/pangolin/${pathType.pangolinID}/issues`;
    }
    case BasePath.MORE: {
      return `/pangolin/${pathType.pangolinID}/more`;
    }
    // Module Pages
    case BasePath.HISTORY_LIST: {
      if (pathType.itemsPath) {
        switch (pathType.itemsPath.kind) {
          case BasePathItems.FILTER_ITEMS:
            return `/pangolin/${pathType.pangolinID}/history/filter`;
          case BasePathItems.VIEW_ITEM:
            return `/pangolin/${pathType.pangolinID}/history/view/${pathType.itemsPath.itemID}`;
          default:
            return UnreachableFunction(pathType.itemsPath);
        }
      }
      return `/pangolin/${pathType.pangolinID}/history`;
    }
    case BasePath.BANDWIDTH_LIST: {
      return `/pangolin/${pathType.pangolinID}/bandwidth`;
    }
    case BasePath.BANDWIDTH_SETTINGS: {
      return `/pangolin/${pathType.pangolinID}/bandwidth/settings`;
    }
    case BasePath.SCHEDULE_LIST: {
      return `/pangolin/${pathType.pangolinID}/schedules`;
    }
    case BasePath.SCHEDULE_ADD: {
      return `/pangolin/${pathType.pangolinID}/schedules/add`;
    }
    case BasePath.SCHEDULE_VIEW: {
      return `/pangolin/${pathType.pangolinID}/schedules/${pathType.scheduleID}`;
    }
    case BasePath.SCHEDULE_EDIT: {
      return `/pangolin/${pathType.pangolinID}/schedules/${pathType.scheduleID}/edit`;
    }
    case BasePath.VPN_LIST: {
      return `/pangolin/${pathType.pangolinID}/vpn`;
    }
    case BasePath.VPN_ADD: {
      return `/pangolin/${pathType.pangolinID}/vpn/add`;
    }
    case BasePath.VPN_EDIT: {
      return `/pangolin/${pathType.pangolinID}/vpn/${pathType.clientID}`;
    }
    // More Pages
    case BasePath.MANAGE_PANGOLIN_LIST: {
      return '/pangolins';
    }
    case BasePath.MANAGE_PANGOLIN_VIEW: {
      return `/pangolin/${pathType.pangolinID}/manage/view`;
    }
    case BasePath.MANAGE_PANGOLIN_EDIT: {
      return `/pangolin/${pathType.pangolinID}/manage/edit`;
    }
    case BasePath.MANAGE_PANGOLIN_INTERCEPT: {
      return `/pangolin/${pathType.pangolinID}/manage/intercept`;
    }
    case BasePath.MANAGE_PANGOLIN_SCANNER: {
      return `/pangolin/${pathType.pangolinID}/manage/scanner`;
    }
    case BasePath.MANAGE_PANGOLIN_UNLINK: {
      return `/pangolin/${pathType.pangolinID}/manage/unlink`;
    }
    case BasePath.SETTINGS: {
      return '/settings';
    }
    case BasePath.SETTINGS_EDIT: {
      return '/settings/edit';
    }
    case BasePath.SETTINGS_PASSWORD_CHANGE: {
      return '/settings/password-change';
    }
    case BasePath.SETTINGS_ACCOUNT_DELETE: {
      return '/settings/account-delete';
    }
    // Default Case
    default: {
      return UnreachableFunction(pathType);
    }
  }
}

export function getOrderedBasePaths() {
  const keys = Object.keys(BasePath).map((key) => +key).filter((key) => !isNaN(key));
  keys.sort((a, b) => a - b);
  return keys;
}
